/**
 * Unwrap API response and extract data
 * Handles common response patterns
 */
export const unwrapQuery = (response) => {
    // If response has success flag
    if (response && typeof response.success !== 'undefined') {
        if (response.success) {
            return {
                data: response.data || response,
                success: true,
                message: response.message,
            };
        } else {
            throw new Error(response.message || 'Request failed');
        }
    }

    // If response is direct data
    return {
        data: response,
        success: true,
    };
};

/**
 * Unwrap mutation response
 * Similar to unwrapQuery but for mutations (POST, PUT, DELETE)
 */
export const unwrapMutation = (response) => {
    return unwrapQuery(response);
};

/**
 * Handle API error - now delegates to unified parseApiError but keeps legacy shape for back-compat
 */
export const handleApiError = (error) => {
    // If already ApiError, return legacy shape
    if (error && error.code && error.statusCode !== undefined) {
        return {
            success: false,
            message: error.message,
            status: error.statusCode,
            code: error.code,
            details: error.details,
            requestId: error.requestId,
            data: error.data,
        };
    }
    if (error && error.apiError) {
        const ae = error.apiError;
        return {
            success: false,
            message: ae.message,
            status: ae.statusCode,
            code: ae.code,
            details: ae.details,
            requestId: ae.requestId,
            data: error.response?.data,
        };
    }
    if (error?.response) {
        const data = error.response.data;
        const msg = data?.message || data?.error || 'خطایی رخ داده است';
        return {
            success: false,
            message: Array.isArray(msg) ? msg.join(', ') : msg,
            status: error.response.status,
            code: data?.code,
            details: data?.details || data?.errors,
            requestId: data?.requestId,
            data,
        };
    } else if (error?.request) {
        return {
            success: false,
            message: 'خطا در برقراری ارتباط با سرور',
            status: 0,
            code: 'NETWORK_ERROR',
        };
    } else {
        return {
            success: false,
            message: error?.message || 'خطای نامشخص',
            status: 0,
            code: 'UNKNOWN',
        };
    }
};

/**
 * Extract a user-facing message from an error
 * Works with TanStack Query queryFn/mutationFn errors. Prefers code-mapped messages.
 * @param {Error|Object} error - The error object
 * @param {string} fallback - Fallback message when none can be derived
 * @returns {string|null}
 */
export const getErrorMessage = (error, fallback = 'خطای نامشخص') => {
    if (!error) return null;
    // Use centralized mapper if available
    try {
        // eslint-disable-next-line global-require
        const { parseApiError: _parse } = require('../common/errors/api-error');
        const { getMessageForCode: _msg } = require('../common/errors/error-message');
        const parsed = _parse(error);
        return _msg(parsed.code, parsed.message) || handleApiError(error).message || fallback;
    } catch {
        return handleApiError(error).message || fallback;
    }
};

export default {
    unwrapQuery,
    unwrapMutation,
    handleApiError,
    getErrorMessage,
};
